import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { Card } from '../../../ui/card';
import { cn } from '../../../ui/utils';
import { PostMenu, type CardMenu } from '../shared/PostMenu';
import {
  DeletedBadge,
  KindBadge,
  Metric,
  PostAuthor,
  PostAvatar,
  PostCaption,
  PostCoauthors,
  PostMeta,
  PostThumb,
  SentimentChip,
  StoryCaptured,
  StoryExpiry,
  StoryFrame,
} from './cells';
import { isStory, type PostEntry } from './readings';

/** `post` stacks a square or 4:5 frame over its text; `tall` puts a 9:16 frame
 *  beside it; `wide` stacks a landscape frame over it. */
export type CardShape = 'post' | 'tall' | 'wide';

/** Reels and stories are vertical by nature; any other post takes its shape
 *  from the stored file once it has loaded, and reads as a post until then. */
export function shapeOf(entry: PostEntry, aspect: number | null): CardShape {
  const { kind } = entry.post;
  if (kind === 'STORY' || kind === 'REEL') return 'tall';
  if (aspect === null) return 'post';
  if (aspect >= 1.3) return 'wide';
  return aspect <= 0.7 ? 'tall' : 'post';
}

function isVideo(entry: PostEntry): boolean {
  return entry.post.media.find((item) => item.source_url !== null)?.media_kind === 'video';
}

/** The post as the reader knows it on the network. A deleted post keeps its
 *  figures and says it is gone. A story carries no caption, figures or
 *  sentiment, and shows when it was captured and when it expires instead. */
export function PostCard(props: {
  entry: PostEntry;
  href?: string;
  menu?: CardMenu;
  shape?: CardShape;
  onAspect?: (ratio: number) => void;
}): ReactNode {
  const { post, figures, sentiment } = props.entry;
  const shape = props.shape ?? shapeOf(props.entry, null);
  const author = <PostAuthor post={post} />;
  const story = isStory(post);
  const tall = shape === 'tall';

  const header = (
    <div className="flex flex-wrap items-center gap-2 px-4 pt-3 pb-2">
      <PostAvatar post={post} />
      <div className="flex min-w-24 flex-1 flex-col gap-1">
        <h2 className="text-body-sm truncate">
          {props.href !== undefined ? (
            <Link
              to={props.href}
              className="focus-visible:after:ring-ring outline-none after:absolute after:inset-0 after:rounded-xl focus-visible:after:ring-3 focus-visible:after:ring-inset"
            >
              {author}
            </Link>
          ) : (
            author
          )}
        </h2>
        <PostMeta post={post} />
      </div>
      <PostCoauthors post={post} />
      <div className="ms-auto flex items-center gap-2">
        <KindBadge post={post} />
        <DeletedBadge post={post} />
        {props.menu !== undefined && (
          // Above the stretched link, so the menu opens instead of the post.
          <span className="relative z-10">
            <PostMenu
              post={post}
              campaignId={props.menu.campaignId}
              tenantId={props.menu.tenantId}
              related={post.related}
              canReclassify={props.menu.canReclassify}
              href={props.href}
            />
          </span>
        )}
      </div>
    </div>
  );

  const text = story ? (
    <div className="text-caption text-muted-foreground flex flex-wrap items-center gap-2 px-4 pt-1 pb-3">
      {post.captured_at !== null && <StoryCaptured capturedAt={post.captured_at} />}
      {post.expires_at !== null && <StoryExpiry expiresAt={post.expires_at} />}
    </div>
  ) : (
    <div className="px-4 pt-1 pb-3">
      <p className={cn('text-body-sm', tall ? 'line-clamp-8' : 'line-clamp-3')}>
        <PostCaption post={post} />
      </p>
    </div>
  );

  const footer = !story && (
    <div className="border-border mt-auto flex flex-wrap items-center gap-x-5 gap-y-2 border-t px-4 py-3">
      <Metric figures={figures} metric="reach" />
      <Metric figures={figures} metric="estimatedReach" />
      <Metric figures={figures} metric="likes" />
      <Metric figures={figures} metric="comments" />
      <Metric figures={figures} metric="shares" />
      <Metric figures={figures} metric="saves" />
      <span className="ms-auto">
        <SentimentChip sentiment={sentiment} />
      </span>
    </div>
  );

  if (tall) {
    return (
      <Card
        data-post={post.post_id}
        data-shape={shape}
        className="relative h-full gap-0 overflow-hidden py-0 @xl:flex-row"
      >
        <div className="flex shrink-0 justify-center bg-black @xl:max-w-media-caption">
          <StoryFrame
            post={post}
            onAspect={props.onAspect}
            className="h-media-portrait max-w-full @xl:h-full @xl:min-h-80 @5xl:min-h-96"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          {header}
          {text}
          {footer}
        </div>
      </Card>
    );
  }

  const frame = isVideo(props.entry) ? (
    <div className={cn('flex justify-center bg-black', shape !== 'wide' && 'flex-1')}>
      <StoryFrame
        post={post}
        onAspect={props.onAspect}
        className={shape === 'wide' ? 'aspect-video w-full' : 'aspect-auto min-h-56 w-full'}
      />
    </div>
  ) : (
    <PostThumb
      post={post}
      onAspect={props.onAspect}
      className={
        shape === 'wide'
          ? 'aspect-video w-full'
          : 'aspect-square w-full flex-1 @xl:aspect-auto @xl:min-h-56'
      }
    />
  );
  return (
    <Card
      data-post={post.post_id}
      data-shape={shape}
      className="relative h-full gap-0 overflow-hidden py-0"
    >
      {frame}
      {header}
      {text}
      {footer}
    </Card>
  );
}
